/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: AI Assistant
 🏢 Company: Ring2All LLC
 📄 File: ThemePreview.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Visual preview components for theme selection with CSS-generated previews.
────────────────────────────────────────────────────────────────*/

import React from 'react';

interface ThemePreviewProps {
  type: 'system' | 'light' | 'dark';
  className?: string;
}

/**
 * Preview for System theme - shows a diagonal split between light and dark
 */
export function SystemThemePreview({ className = '' }: { className?: string }) {
  return (
    <div className={`relative w-full h-20 rounded-lg overflow-hidden ${className}`}>
      {/* Light side (top-left) */}
      <div className="absolute inset-0 bg-white" style={{ clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}>
        <div className="p-2 space-y-1 h-full">
          <div className="h-1 bg-gray-300 rounded" style={{ width: '60%' }} />
          <div className="h-1 bg-gray-300 rounded" style={{ width: '80%' }} />
          <div className="h-1 bg-gray-300 rounded" style={{ width: '45%' }} />
          <div className="flex gap-1.5 mt-2">
            <div className="w-2 h-2 bg-gray-300 rounded-full" />
            <div className="h-1 bg-gray-300 rounded flex-1" />
          </div>
        </div>
      </div>
      {/* Dark side (bottom-right) */}
      <div className="absolute inset-0 bg-gray-900" style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}>
        <div className="p-2 space-y-1 h-full">
          <div className="h-1 bg-gray-700 rounded" style={{ width: '60%' }} />
          <div className="h-1 bg-gray-700 rounded" style={{ width: '80%' }} />
          <div className="h-1 bg-gray-700 rounded" style={{ width: '45%' }} />
          <div className="flex gap-1.5 mt-2">
            <div className="w-2 h-2 bg-gray-700 rounded-full" />
            <div className="h-1 bg-gray-700 rounded flex-1" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Preview for Light theme
 */
export function LightThemePreview({ className = '' }: { className?: string }) {
  return (
    <div className={`w-full h-20 rounded-lg bg-white border border-gray-200 ${className}`}>
      <div className="p-2 space-y-1 h-full">
        <div className="h-1 bg-gray-300 rounded" style={{ width: '60%' }} />
        <div className="h-1 bg-gray-300 rounded" style={{ width: '80%' }} />
        <div className="h-1 bg-gray-300 rounded" style={{ width: '45%' }} />
        <div className="flex gap-1.5 mt-2">
          <div className="w-2 h-2 bg-gray-300 rounded-full" />
          <div className="h-1 bg-gray-300 rounded flex-1" />
        </div>
      </div>
    </div>
  );
}

/**
 * Preview for Dark theme
 */
export function DarkThemePreview({ className = '' }: { className?: string }) {
  return (
    <div className={`w-full h-20 rounded-lg bg-gray-900 border border-gray-800 ${className}`}>
      <div className="p-2 space-y-1 h-full">
        <div className="h-1 bg-gray-700 rounded" style={{ width: '60%' }} />
        <div className="h-1 bg-gray-700 rounded" style={{ width: '80%' }} />
        <div className="h-1 bg-gray-700 rounded" style={{ width: '45%' }} />
        <div className="flex gap-1.5 mt-2">
          <div className="w-2 h-2 bg-gray-700 rounded-full" />
          <div className="h-1 bg-gray-700 rounded flex-1" />
        </div>
      </div>
    </div>
  );
}

/**
 * Main component that renders the appropriate preview based on type
 */
export function ThemePreview({ type, className = '' }: ThemePreviewProps) {
  switch (type) {
    case 'system':
      return <SystemThemePreview className={className} />;
    case 'light':
      return <LightThemePreview className={className} />;
    case 'dark':
      return <DarkThemePreview className={className} />;
    default:
      return <LightThemePreview className={className} />;
  }
}

/**
 * Light color scheme previews
 */
interface LightColorSchemePreviewProps {
  scheme: 'slate' | 'gray' | 'neutral' | 'white' | 'very-light-green' | 'very-light-purple' | 'light-green';
  className?: string;
}

const lightColorSchemes = {
  slate: {
    bg: '#f8fafc',
    border: '#e2e8f0',
    elements: '#cbd5e1'
  },
  gray: {
    bg: '#f9fafb',
    border: '#e5e7eb',
    elements: '#9ca3af'
  },
  neutral: {
    bg: '#fafafa',
    border: '#e5e5e5',
    elements: '#a3a3a3'
  },
  white: {
    bg: '#ffffff',
    border: '#e2e8f0',
    elements: '#cbd5e1'
  },
  'very-light-green': {
    bg: '#f0f9f0',
    border: '#d4e6d4',
    elements: '#a8d5a8'
  },
  'very-light-purple': {
    bg: '#f5f0f9',
    border: '#d4d4e6',
    elements: '#b8b8d4'
  },
  'light-green': {
    bg: '#D6E4DD',
    border: '#b8d4c8',
    elements: '#9ac4b3'
  }
};

export function LightColorSchemePreview({ scheme, className = '' }: LightColorSchemePreviewProps) {
  const colors = lightColorSchemes[scheme];
  
  return (
    <div 
      className={`w-full h-12 rounded-lg ${className}`}
      style={{ 
        backgroundColor: colors.bg, 
        border: `1px solid ${colors.border}` 
      }}
    >
      <div className="p-1.5 space-y-0.5 h-full">
        <div className="h-0.5 rounded" style={{ width: '60%', backgroundColor: colors.elements }} />
        <div className="h-0.5 rounded" style={{ width: '80%', backgroundColor: colors.elements }} />
        <div className="h-0.5 rounded" style={{ width: '45%', backgroundColor: colors.elements }} />
        <div className="flex gap-1 mt-1">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.elements }} />
          <div className="h-0.5 rounded flex-1" style={{ backgroundColor: colors.elements }} />
        </div>
      </div>
    </div>
  );
}

/**
 * Dark color scheme previews
 */
interface DarkColorSchemePreviewProps {
  scheme: 'mint' | 'navy' | 'mirage' | 'cinder' | 'black';
  className?: string;
}

const darkColorSchemes = {
  mint: {
    bg: '#0f172a',
    border: '#1e293b',
    elements: '#475569'
  },
  navy: {
    bg: '#1e293b',
    border: '#334155',
    elements: '#64748b'
  },
  mirage: {
    bg: '#0f1419',
    border: '#1a1f2e',
    elements: '#334155'
  },
  cinder: {
    bg: '#1c1c1c',
    border: '#2a2a2a',
    elements: '#4a4a4a'
  },
  black: {
    bg: '#000000',
    border: '#1a1a1a',
    elements: '#2a2a2a'
  }
};

export function DarkColorSchemePreview({ scheme, className = '' }: DarkColorSchemePreviewProps) {
  const colors = darkColorSchemes[scheme];
  
  return (
    <div 
      className={`w-full h-20 rounded-lg ${className}`}
      style={{ 
        backgroundColor: colors.bg, 
        border: `1px solid ${colors.border}` 
      }}
    >
      <div className="p-2 space-y-1 h-full">
        <div className="h-1 rounded" style={{ width: '60%', backgroundColor: colors.elements }} />
        <div className="h-1 rounded" style={{ width: '80%', backgroundColor: colors.elements }} />
        <div className="h-1 rounded" style={{ width: '45%', backgroundColor: colors.elements }} />
        <div className="flex gap-1.5 mt-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.elements }} />
          <div className="h-1 rounded flex-1" style={{ backgroundColor: colors.elements }} />
        </div>
      </div>
    </div>
  );
}

